// Écrans utilitaires : chargement (pendant l'auth) et erreur. L'accueil et le jeu
// sont gérés par gameboy.ts / game-screen.ts.
import { tg, haptic } from "./telegram";
import { UI, type Lang } from "./game/i18n";
import type { AuthResult } from "./auth";

function clear(root: HTMLElement): void {
  root.replaceChildren();
  tg?.MainButton.hide();
  tg?.BackButton.hide();
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** Écran de chargement (pendant la vérification de l'auth). */
export function renderLoading(root: HTMLElement): void {
  clear(root);
  const wrap = el("div", "screen center");
  wrap.append(el("div", "spinner"));
  wrap.append(el("p", "muted", "Connexion à Telegram…"));
  root.append(wrap);
}

/** Mini-tuto (1ᵉʳ lancement) : explique le but, résolu au tap sur JOUER. */
export function showIntro(root: HTMLElement, lang: Lang): Promise<void> {
  return new Promise((resolve) => {
    clear(root);
    const ui = UI[lang];
    const wrap = el("div", "screen center");
    wrap.append(el("div", "intro-emoji", "👃☝️"));
    wrap.append(el("h2", "title", ui.howTo));
    wrap.append(el("p", "muted", ui.instruction));
    const btn = el("button", "cta", ui.menuPlay);
    btn.addEventListener("click", () => {
      haptic("light");
      resolve();
    });
    wrap.append(btn);
    root.append(wrap);
  });
}

/** Popup daily streak (1re connexion du jour) : série + pièces offertes. */
export function showStreak(root: HTMLElement, lang: Lang, days: number, reward: number): Promise<void> {
  return new Promise((resolve) => {
    clear(root);
    const ui = UI[lang];
    const wrap = el("div", "screen center");
    wrap.append(el("div", "intro-emoji", "🔥"));
    wrap.append(el("h2", "title", ui.streakTitle.replace("{n}", String(days))));
    wrap.append(el("p", "streak-reward", `+${reward} 🪙`));
    // Aperçu de la série : les 7 jours, ceux déjà faits en feu.
    const dots = el("p", "muted");
    dots.textContent = Array.from({ length: 7 }, (_, i) => (i < Math.min(days, 7) ? "🔥" : "·")).join(" ");
    wrap.append(dots);
    const btn = el("button", "cta", ui.ok);
    btn.addEventListener("click", () => {
      haptic("success");
      resolve();
    });
    wrap.append(btn);
    root.append(wrap);
  });
}

/** Écran d'erreur : explique pourquoi l'app n'a pas pu démarrer. */
export function renderError(root: HTMLElement, result: Extract<AuthResult, { ok: false }>): void {
  clear(root);
  haptic("error");
  const wrap = el("div", "screen center");

  let emoji = "🚫";
  let heading = "Impossible de démarrer";
  let detail = "";

  if (result.reason === "no-telegram" || result.reason === "no-initdata") {
    emoji = "📱";
    heading = "Ouvre cette app depuis Telegram";
    detail =
      "The Big Dig se joue à l'intérieur de Telegram. Lance-la depuis le bot ou le menu de l'app.";
  } else {
    heading = "Connexion refusée";
    detail =
      "Le serveur n'a pas pu vérifier ta session" +
      (result.error ? ` (${result.error}).` : ".");
  }

  wrap.append(el("div", "big-emoji", emoji));
  wrap.append(el("h2", "title", heading));
  wrap.append(el("p", "muted", detail));

  if (result.reason !== "api" && import.meta.env.DEV) {
    wrap.append(
      el("p", "muted small dev-note", "DEV : ouvert hors Telegram — initData absent, c'est attendu."),
    );
  }

  root.append(wrap);
}
